/**
 * Ask layer core: framework-neutral HTTP handlers.
 *
 * The contract, independent of framework:
 *   POST /search    body: AssistantSearchRequest  → 200 AssistantSearchResponse | 400 ValidationErrorBody
 *   POST /feedback  body: AssistantFeedback       → 202 { recorded: true }      | 400 ValidationErrorBody
 *
 * Both are POST rather than GET: the question is free text a user typed, and query strings end up
 * in access logs and browser history. Search is still a pure read — nothing here writes.
 *
 * Who is asking comes from the ADAPTER (its auth middleware, its session), never from the body:
 * roles rank-boost results and a caller could otherwise boost their own. A null principal is the
 * unauthenticated fallback — the panel sits behind your login, so that should not happen in
 * production, and the layer answers anyway.
 
 */
import { recordFeedback, search } from "./assistant";
import type { Principal, TenantId } from "./types";
import { validateFeedback, validateSearchRequest, type ValidationIssue } from "./validate";

export interface HandlerResult {
  status: number;
  body: unknown;
}

export interface ValidationErrorBody {
  error: {
    code: "VALIDATION_ERROR";
    message: string;
    issues: ValidationIssue[];
  };
}

const invalid = (issues: ValidationIssue[]): HandlerResult => ({
  status: 400,
  body: {
    error: { code: "VALIDATION_ERROR", message: "request body is invalid", issues },
  } satisfies ValidationErrorBody,
});

/**
 * Wrap the success body here if a server deployment ever adds a response envelope — e.g. `({ data })` —
 * and unwrap it in `ask-api.ts`'s `post()`. Shipped as identity so the HTTP client template works
 * against the Express adapter unchanged.
 */
const envelope = (body: unknown): unknown => body;

export const handleSearch = async (
  body: unknown,
  principal: Principal | null,
  tenantId: TenantId
): Promise<HandlerResult> => {
  const parsed = validateSearchRequest(body);
  if (!parsed.ok) return invalid(parsed.issues);
  const response = search({
    ...parsed.value,
    // Roles rank-boost the results; they never hide an entry. A user asking about something
    // they cannot do still gets the answer, with its prerequisites stated on the card.
    roles: principal?.roles ?? [],
    userId: principal?.id,
    tenantId,
  });
  return { status: 200, body: envelope(response) };
};

export const handleFeedback = async (
  body: unknown,
  principal: Principal | null,
  tenantId: TenantId
): Promise<HandlerResult> => {
  const parsed = validateFeedback(body);
  if (!parsed.ok) return invalid(parsed.issues);
  recordFeedback({ ...parsed.value, userId: principal?.id, tenantId });
  return { status: 202, body: envelope({ recorded: true }) };
};
